import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowForward,
  CheckCircleOutline,
  Schedule,
  AttachMoney,
  Headset,
  PowerSettingsNew,
  Wifi,
  VolumeUp,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import setsyncBoxImage from '../../assets/setsync_box.png';
import Scanlines from '../ui/Scanlines';
import HardwareBox from '../ui/HardwareBox';
import ProblemCard from '../ui/ProblemCard';

// Benefit Item
const BenefitItem = ({ text }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
    <CheckCircleOutline sx={{ color: 'primary.main', fontSize: 20, mt: 0.3 }} />
    <Typography sx={{ color: '#CCCCCC', lineHeight: 1.6 }}>{text}</Typography>
  </Box>
);

const VenuesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const problems = [
    {
      icon: <AttachMoney />,
      title: 'Unprogrammed Off-Nights',
      description: 'Add a paid DJ set when the alternative is no DJ programming at all.',
    },
    {
      icon: <Schedule />,
      title: 'Last-Minute Gaps',
      description: 'Explore paid DJ coverage when a cancellation or emergency creates an opening.',
    },
    {
      icon: <Headset />,
      title: 'New Programming Ideas',
      description: 'Bring in specialty programming and new sounds beyond your market.',
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Scanlines />

      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 16, md: 20 },
          pb: { xs: 10, md: 16 },
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  color: 'primary.main',
                  fontWeight: 700,
                  letterSpacing: '0.3em',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  mb: 2,
                }}
              >
                For Venues
              </Typography>

              <Typography
                variant="h1"
                sx={{
                  color: 'text.primary',
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                Add Live DJ Programming,{' '}
                <Box component="span" sx={{ color: 'primary.main' }}>
                  Responsibly
                </Box>
              </Typography>

              <Typography
                sx={{
                  color: 'text.secondary',
                  fontFamily: '"Space Mono", monospace',
                  mb: 5,
                  maxWidth: 500,
                  lineHeight: 1.8,
                  fontSize: { xs: '0.9rem', md: '1rem' },
                }}
              >
                Fill true programming gaps with responsibly paid working DJs when
                an in-room booking is impractical, while keeping local relationships central.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/register"
                  endIcon={<ArrowForward />}
                  sx={{ px: 4, py: 1.5 }}
                >
                  Join as Founding Venue
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/djs"
                  sx={{ px: 4, py: 1.5 }}
                >
                  For DJs
                </Button>
              </Box>
            </Grid>

            {!isMobile && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <HardwareBox image={setsyncBoxImage} />
                </Box>
              </Grid>
            )}
          </Grid>
        </Container>
      </Box>

      {/* Problem Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          bgcolor: 'secondary.dark',
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'secondary.main',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: 8, textAlign: 'center' }}>
            <Typography
              sx={{
                fontFamily: '"Space Mono", monospace',
                color: 'primary.main',
                fontWeight: 700,
                letterSpacing: '0.3em',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              The Problem
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: 'text.primary',
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              Add to Your Calendar — Not Replace Your Community
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {problems.map((problem, index) => (
              <Grid item xs={12} md={4} key={index}>
                <ProblemCard {...problem} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Solution Section */}
      <Box sx={{ py: { xs: 10, md: 16 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  fontFamily: '"Space Mono", monospace',
                  color: 'primary.main',
                  fontWeight: 700,
                  letterSpacing: '0.3em',
                  fontSize: '0.7rem',
                  textTransform: 'uppercase',
                  mb: 2,
                }}
              >
                The Solution
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  color: 'text.primary',
                  mb: 4,
                  fontSize: { xs: '2rem', md: '2.5rem' },
                }}
              >
                More Ways to Book Working DJs
              </Typography>

              <BenefitItem text="Auto-failover keeps the music playing if anything disconnects" />
              <BenefitItem text="Create new paid hours on off-nights or provide cancellation and emergency coverage" />
              <BenefitItem text="Offer specialty programming, guest sets, and cross-market collaboration" />
              <BenefitItem text="Build remote residencies that expand your calendar and support working DJs" />
              <BenefitItem text="Complement established local bookings instead of displacing them to reduce costs" />
              <BenefitItem text="US-based support during your business hours" />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 4,
                  bgcolor: 'background.paper',
                  border: '2px solid',
                  borderColor: 'secondary.light',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 4,
                    height: '100%',
                    bgcolor: 'primary.main',
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontFamily: '"Bebas Neue", sans-serif',
                    color: 'text.primary',
                    mb: 3,
                    letterSpacing: '0.05em',
                  }}
                >
                  The SetSync Box
                </Typography>
                
                {/* Product image in solution section */}
                <Box
                  sx={{
                    mb: 3,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    component="img"
                    src={setsyncBoxImage}
                    alt="SetSync Box"
                    sx={{
                      width: '100%',
                      maxWidth: 300,
                      height: 'auto',
                      filter: 'drop-shadow(0 10px 20px rgba(0, 0, 0, 0.4))',
                    }}
                  />
                </Box>
                
                <Typography
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  This isn't a laptop or iPad that a bartender can accidentally
                  close. It's a dedicated appliance that connects to your PA
                  system and internet router. If anything disconnects — the DJ,
                  the internet, anything — it automatically fails over to keep
                  the music playing until you're ready to stop.
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PowerSettingsNew sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography sx={{ color: '#CCCCCC', fontSize: '0.85rem' }}>
                      Auto-failover
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Wifi sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography sx={{ color: '#CCCCCC', fontSize: '0.85rem' }}>
                      Jitter Buffer
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VolumeUp sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography sx={{ color: '#CCCCCC', fontSize: '0.85rem' }}>
                      Studio Quality
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Credit System Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          bgcolor: 'secondary.dark',
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'secondary.main',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              sx={{
                fontFamily: '"Space Mono", monospace',
                color: 'primary.main',
                fontWeight: 700,
                letterSpacing: '0.3em',
                fontSize: '0.7rem',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              Simple Pricing
            </Typography>
            <Typography
              variant="h2"
              sx={{
                color: 'text.primary',
                fontSize: { xs: '2rem', md: '2.5rem' },
                mb: 3,
              }}
            >
              Pay for What You Use
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                fontFamily: '"Space Mono", monospace',
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.8,
              }}
            >
              1 Performance Credit = 1 Hour of Live Streaming. Buy a monthly
              credit pack. Book DJs. Done.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/register"
              endIcon={<ArrowForward />}
              sx={{ px: 4, py: 1.5 }}
            >
              Apply as Founding Venue
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 12, md: 20 } }}>
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 4, md: 8 },
              border: '2px solid',
              borderColor: 'secondary.light',
              bgcolor: 'rgba(13, 13, 13, 0.9)',
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -2,
                left: '10%',
                right: '10%',
                height: 4,
                bgcolor: 'primary.main',
              },
            }}
          >
            <Typography
              variant="h2"
              sx={{
                color: 'text.primary',
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' },
              }}
            >
              Ready to Add to Your DJ Calendar?
            </Typography>
            <Typography
              sx={{
                color: 'text.secondary',
                fontFamily: '"Space Mono", monospace',
                mb: 5,
                maxWidth: 500,
                mx: 'auto',
                lineHeight: 1.8,
              }}
            >
              Create new programming and new paid DJ opportunities while keeping
              established local relationships at the center.
            </Typography>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/register"
              endIcon={<ArrowForward />}
              sx={{ px: 5, py: 1.5 }}
            >
              Get Hardware
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default VenuesPage;
